import { createFileRoute } from "@tanstack/react-router"

import { CodeBlock } from "@/components/dev/CodeBlock"
import { PropsTable } from "@/components/dev/PropsTable"
import Card from "@/globalComponents/atoms/Card"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Text from "@/globalComponents/atoms/Text"

export const Route = createFileRoute("/qr/$qrLookupID/dev/atoms/icon")({
  staticData: {
    pageName: "Icon Component",
  },
  component: RouteComponent,
})
//TODO show how to use icon coloring
function RouteComponent() {
  return (
    <>
      <style>{`
        .dev-container > *:not(:first-child) {
          margin-top: var(--space-5);
        }
        .icon-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
          gap: var(--space-3);
        }
        .icon-demo {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: var(--space-3);
          border: 1px solid var(--neutral-6);
          border-radius: var(--radius-2);
        }
        .icon-demo-name {
          margin-top: var(--space-2);
          text-align: center;
          font-size: 12px;
        }
      `}</style>
      <div className="dev-container">
        <Card>
          <Heading level={1} as={2}>
            Icon Component
          </Heading>
          <Text>
            The Icon component provides access to the icon library with
            consistent sizing, coloring, and theming. Icons are lazy-loaded for
            performance and support various sizes and slots for use in buttons
            and other components.
          </Text>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Icon Sizes
          </Heading>
          <Text>
            Icons come in four sizes: small, default, large, and x-large
          </Text>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-4)",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <Icon name="parking" size="small" />
              <Text level={3}>Small</Text>
            </div>
            <div style={{ textAlign: "center" }}>
              <Icon name="parking" size="default" />
              <Text level={3}>Default</Text>
            </div>
            <div style={{ textAlign: "center" }}>
              <Icon name="parking" size="large" />
              <Text level={3}>Large</Text>
            </div>
            <div style={{ textAlign: "center" }}>
              <Icon name="parking" size="x-large" />
              <Text level={3}>X-Large</Text>
            </div>
          </div>
          <CodeBlock>{`<Icon name="parking" size="small" />
<Icon name="parking" size="default" />
<Icon name="parking" size="large" />
<Icon name="parking" size="x-large" />`}</CodeBlock>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Icons in Buttons
          </Heading>
          <Text>
            Use the <code>slot</code> prop to position icons in buttons. The
            slot adds appropriate margins automatically.
          </Text>
          <CodeBlock>{`<Button buttonType="primary" onPress={() => {}}>
  <Icon name="parking" slot="left" />
  Buy Parking
</Button>

<Button buttonType="primary" onPress={() => {}}>
  Continue
  <Icon name="arrowRight" slot="right" />
</Button>`}</CodeBlock>
          <Text level={3}>
            See the Button component page for interactive examples
          </Text>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Common Icons
          </Heading>
          <Text>
            Here are the most commonly used icons in the Tap N Samba application
          </Text>
          <div className="icon-grid">
            <div className="icon-demo">
              <Icon name="parking" />
              <Text className="icon-demo-name" as="span">
                parking
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="car" />
              <Text className="icon-demo-name" as="span">
                car
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="calendar" />
              <Text className="icon-demo-name" as="span">
                calendar
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="clock" />
              <Text className="icon-demo-name" as="span">
                clock
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="creditCard" />
              <Text className="icon-demo-name" as="span">
                creditCard
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="file" />
              <Text className="icon-demo-name" as="span">
                file
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="check" />
              <Text className="icon-demo-name" as="span">
                check
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="close" />
              <Text className="icon-demo-name" as="span">
                close
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="accessible" />
              <Text className="icon-demo-name" as="span">
                help
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="info" />
              <Text className="icon-demo-name" as="span">
                info
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="warning" />
              <Text className="icon-demo-name" as="span">
                warning
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="select" />
              <Text className="icon-demo-name" as="span">
                settings
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="person" />
              <Text className="icon-demo-name" as="span">
                person
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="search" />
              <Text className="icon-demo-name" as="span">
                search
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="arrowLeft" />
              <Text className="icon-demo-name" as="span">
                arrowLeft
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="arrowRight" />
              <Text className="icon-demo-name" as="span">
                arrowRight
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="arrowUp" />
              <Text className="icon-demo-name" as="span">
                arrowUp
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="arrowDown" />
              <Text className="icon-demo-name" as="span">
                arrowDown
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="chevronLeft" />
              <Text className="icon-demo-name" as="span">
                chevronLeft
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="chevronRight" />
              <Text className="icon-demo-name" as="span">
                chevronRight
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="chevronUp" />
              <Text className="icon-demo-name" as="span">
                chevronUp
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="chevronDown" />
              <Text className="icon-demo-name" as="span">
                chevronDown
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="hamburger" />
              <Text className="icon-demo-name" as="span">
                menu
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="upload" />
              <Text className="icon-demo-name" as="span">
                location
              </Text>
            </div>
          </div>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Navigation Icons
          </Heading>
          <div className="icon-grid">
            <div className="icon-demo">
              <Icon name="home" />
              <Text className="icon-demo-name" as="span">
                home
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="pass" />
              <Text className="icon-demo-name" as="span">
                passes
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="receipt" />
              <Text className="icon-demo-name" as="span">
                receipt
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="moneySymbol" />
              <Text className="icon-demo-name" as="span">
                wallet
              </Text>
            </div>
          </div>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Payment Icons
          </Heading>
          <Text level={3}>
            Payment method icons should use <code>retainOriginalColors</code> to
            preserve brand colors (except generic creditCard icon).
          </Text>
          <div className="icon-grid">
            <div className="icon-demo">
              <Icon name="creditCard" />
              <Text className="icon-demo-name" as="span">
                creditCard
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="paymentApplePay" retainOriginalColors />
              <Text className="icon-demo-name" as="span">
                applePay
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="paymentGooglePay" retainOriginalColors />
              <Text className="icon-demo-name" as="span">
                googlePay
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="paymentVisa" retainOriginalColors />
              <Text className="icon-demo-name" as="span">
                visa
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="paymentMastercard" retainOriginalColors />
              <Text className="icon-demo-name" as="span">
                mastercard
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="paymentAmericanExpress" retainOriginalColors />
              <Text className="icon-demo-name" as="span">
                americanExpress
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="paymentDiscover" retainOriginalColors />
              <Text className="icon-demo-name" as="span">
                discover
              </Text>
            </div>
          </div>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Company Icons
          </Heading>
          <Text level={3}>
            Company icons should always use <code>retainOriginalColors</code> to
            preserve brand colors.
          </Text>
          <div className="icon-grid">
            <div className="icon-demo">
              <Icon name="companiesAvigilon" retainOriginalColors />
              <Text className="icon-demo-name" as="span">
                avigilon
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="companiesBallparc" retainOriginalColors />
              <Text className="icon-demo-name" as="span">
                ballparc
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="companiesMPS" retainOriginalColors />
              <Text className="icon-demo-name" as="span">
                MPS
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="companiesT2" retainOriginalColors />
              <Text className="icon-demo-name" as="span">
                T2
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="companiesVigilant" retainOriginalColors />
              <Text className="icon-demo-name" as="span">
                vigilant
              </Text>
            </div>
          </div>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Flag Icons
          </Heading>
          <Text level={3}>
            Flag icons should always use <code>retainOriginalColors</code> to
            preserve accurate flag colors.
          </Text>
          <div className="icon-grid">
            <div className="icon-demo">
              <Icon name="flagUnitedStates" retainOriginalColors />
              <Text className="icon-demo-name" as="span">
                United States
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="flagCanada" retainOriginalColors />
              <Text className="icon-demo-name" as="span">
                Canada
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="flagUnitedKingdom" retainOriginalColors />
              <Text className="icon-demo-name" as="span">
                United Kingdom
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="flagMexico" retainOriginalColors />
              <Text className="icon-demo-name" as="span">
                Mexico
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="flagFrance" retainOriginalColors />
              <Text className="icon-demo-name" as="span">
                France
              </Text>
            </div>
            <div className="icon-demo">
              <Icon name="flagGermany" retainOriginalColors />
              <Text className="icon-demo-name" as="span">
                Germany
              </Text>
            </div>
          </div>
          <Text level={3} style={{ marginTop: "var(--space-3)" }}>
            200+ additional country flags available
          </Text>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Props
          </Heading>
          <PropsTable
            props={[
              {
                name: "name",
                type: "IconName",
                default: "Required",
                description:
                  "Icon name from the icon library (332 available icons)",
              },
              {
                name: "size",
                type: '"small" | "default" | "large" | "x-large"',
                default: '"default"',
                description: "Size of the icon",
              },
              {
                name: "slot",
                type: '"left" | "right" | "solo"',
                default: "undefined",
                description:
                  "Position slot for use in buttons - adds appropriate margins",
              },
              {
                name: "className",
                type: "string",
                default: "undefined",
                description: "Additional CSS classes",
              },
              {
                name: "retainOriginalColors",
                type: "boolean",
                default: "false",
                description:
                  "Keep original SVG colors instead of applying theme colors",
              },
              {
                name: "style",
                type: "CSSProperties",
                default: "undefined",
                description: "Inline styles",
              },
            ]}
          />
        </Card>
      </div>
    </>
  )
}
